// @flow
import React, { Component, Fragment } from 'react';
import { flattenDeep, get } from 'lodash';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import {
  ICONS,
  MEDIA_TYPES,
  ORDERED_MEDIAS,
  ORDERED_MEDIAS_THUMBNAILS,
} from 'Components/utils/medias/medias';

import { ActionButtonsGroup } from 'Components/frameworks/action-buttons-group/ActionButtonsGroup.jsx';
import { Avatar } from 'Components/frameworks/avatar/Avatar.jsx';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop.jsx';
import { Carousel } from 'Components/frameworks/carousel/Carousel.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { Video } from 'Components/frameworks/video/Video.jsx';

import { MultimediaLauncher } from 'Components/widgets/multimedia-launcher/MultimediaLauncher.jsx';

export type MultimediaResources = {
  photos?: Array<{
    url: string,
  }>,
  socialPictures?: Array<SocialPicture>,
  videos?: Array<{
    thumbnailUrl: string,
    url: string,
  }>,
  videos360?: Array<{
    thumbnailUrl: string,
    url: string,
  }>,
};

type Props = {
  galleryLabel?: string,
  closeLabel?: string,
  isOverlay?: boolean,
  labels?: {
    photos?: string,
    socialPictures?: string,
    videos?: string,
    videos360?: string,
  },
  medias: MultimediaResources,
  nextLabel?: string,
  previousLabel?: string,
  ratioKey: string,
};

type State = {
  activeMedias: string,
  carouselIndex: number,
};

export class Multimedia extends Component<Props, State> {
  static defaultProps = {
    medias: {},
    ratioKey: ratioUtils.L,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      activeMedias: '',
      carouselIndex: 0,
    };
  }

  closeCarousel = () => {
    this.setState({ activeMedias: '', carouselIndex: 0 });
  };

  showCarousel = (type: string, index: number = 0) => {
    this.setState({ activeMedias: type, carouselIndex: index });
  };

  /* Carousel */
  getCarouselItems(type: string) {
    return (this.props.medias[type] || []).map(item => {
      const { userName, url } = item;

      if (type === MEDIA_TYPES.PHOTOS) {
        return (
          <div
            data-testid="MultimediaPhoto"
            key={url}
            className="flex flex-1 h-full items-center justify-center max-w-full w-full overflow-hidden relative"
          >
            <Image
              changeWidthButHeightIsAuto
              className="absolute top-1/2 left-1/2 max-w-full max-h-full -translate-50"
              imageUrl={url}
            />
          </div>
        );
      }
      if (type === MEDIA_TYPES.SOCIALPICTURES) {
        const { imageLarge } = item;
        return (
          <div
            data-testid="MultimediaSocialPicture"
            key={imageLarge}
            className="flex flex-1 h-full items-center justify-center max-w-full w-full overflow-hidden relative"
          >
            <Image
              changeWidthButHeightIsAuto
              className="absolute top-1/2 left-1/2 max-w-full max-h-full -translate-50"
              forceImage
              imageUrl={imageLarge}
            />
            <div className="absolute inset-x-0 bottom-0 p-4 text-center text-white w-full gradient-linear-transparent-black-80">
              <Avatar userName={userName} />
            </div>
          </div>
        );
      }
      return (
        <Video
          className="flex flex-1 h-full items-center justify-center m-auto md:max-w-4/5 overflow-hidden static md:w-4/5"
          key={url}
          url={url}
        />
      );
    });
  }

  /* Medias */
  hasMedias(isDesktop: boolean): boolean {
    return Object.keys(this.props.medias).reduce((has, type) => {
      return has || this.hasTypeMedias(type, isDesktop);
    }, false);
  }

  hasTypeMedias(type: string, isDesktop: boolean): boolean {
    const hasTypeMedias =
      Array.isArray(get(this.props, ['medias', type])) &&
      get(this.props, ['medias', type]).length > 0;

    if (type === MEDIA_TYPES.VIDEOS360) {
      return hasTypeMedias && isDesktop;
    }
    return hasTypeMedias;
  }

  mapThumbnails(type: string, isDesktop: boolean): Array<*> {
    const medias = this.hasTypeMedias(type, isDesktop) ? this.props.medias[type] : [];
    return medias.map((media, index) => {
      return {
        alt: '',
        hasForcedImage: !!media.thumbnailUrl,
        iconName: type === MEDIA_TYPES.PHOTOS ? '' : ICONS[type],
        onClick: () => this.showCarousel(type, index),
        ratio: this.props.ratioKey,
        src: media.thumbnailUrl || media.url || media.imageLarge || '',
        trackingData: {
          actionGA: 'media',
          labelGA: type,
        },
      };
    });
  }

  getLauncherThumbnails(isDesktop: boolean): Array<*> {
    return flattenDeep(
      ORDERED_MEDIAS_THUMBNAILS.map(type => {
        return this.mapThumbnails(type, isDesktop);
      }),
    ).filter(i => !!i.src);
  }

  getLauncherProps(isDesktop: boolean) {
    const { labels = {}, medias = {} } = this.props;
    return {
      items: ORDERED_MEDIAS.map(type => {
        return this.hasTypeMedias(type, isDesktop)
          ? {
              iconName: ICONS[type],
              id: type,
              isActive: type === this.state.activeMedias,
              label: labels[type],
              number: get(medias, [type], []).length,
              onClick: () => this.showCarousel(type),
              trackingData: {
                actionGA: 'media',
                labelGA: type,
              },
            }
          : null;
      }).filter(Boolean),
      // $FlowFixMe
      thumbnails: this.getLauncherThumbnails(isDesktop).slice(0, 5),
    };
  }

  render() {
    return (
      <ResponsiveContext.Consumer>
        {({ isDesktop }) => {
          if (!this.hasMedias(isDesktop)) return null;

          const { activeMedias, carouselIndex } = this.state;
          const { closeLabel, galleryLabel, isOverlay, nextLabel, previousLabel } = this.props;

          const items = this.getCarouselItems(activeMedias);

          const launcherProps = this.getLauncherProps(isDesktop);

          return (
            <Fragment>
              <MultimediaLauncher
                hasLazyLoading
                isOverlay={isOverlay}
                items={launcherProps.items}
                thumbnails={launcherProps.thumbnails}
              />
              {!!activeMedias && (
                <Backdrop
                  ariaLabel={galleryLabel}
                  className="frosted-glass-desaturated"
                  closeLabel={closeLabel}
                  hasCloseButton
                  onClose={this.closeCarousel}
                >
                  <div className="h-full w-full flex flex-col">
                    <Carousel
                      className="flex-1 flex"
                      hasInfiniteScroll
                      initialIndex={carouselIndex}
                      key={Math.random()}
                      nextLabel={nextLabel}
                      previousLabel={previousLabel}
                    >
                      {items}
                    </Carousel>
                    <div
                      className="flex items-center justify-center px-2"
                      style={{ height: '72px' }}
                    >
                      <ActionButtonsGroup
                        isScrollable
                        items={launcherProps.items}
                        minWidth="6.25rem"
                        theme="overlay"
                      />
                    </div>
                  </div>
                </Backdrop>
              )}
            </Fragment>
          );
        }}
      </ResponsiveContext.Consumer>
    );
  }
}
